import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useApp, api } from '@/context/AppContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  ArrowLeft, Video, Upload, Sparkles, Loader2, FileText,
  CheckCircle2, AlertTriangle, ArrowRight, Award, Trophy,
} from 'lucide-react';
import { toast } from 'sonner';

export default function VideoAnalysisPage() {
  const { t, lang } = useApp();
  const [params] = useSearchParams();
  const [exercises, setExercises] = useState([]);
  const [students, setStudents] = useState([]);
  const [exoId, setExoId] = useState(params.get('ex') || '');
  const [studentId, setStudentId] = useState('');
  const [notes, setNotes] = useState('');
  const [file, setFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [progress, setProgress] = useState(0);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get('/exercises').then(r => setExercises(r.data)).catch(() => {});
    api.get('/students').then(r => setStudents(r.data)).catch(() => {});
  }, []);

  const onFileChange = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 100 * 1024 * 1024) { toast.error(lang === 'ar' ? 'الحد الأقصى 100MB' : 'Max 100MB'); return; }
    setFile(f);
    setFilePreview(URL.createObjectURL(f));
  };

  const submit = async () => {
    if (!exoId) { toast.error(lang === 'ar' ? 'اختر التمرين' : 'Pick an exercise'); return; }
    if (!file) { toast.error(lang === 'ar' ? 'ارفع فيديو' : 'Upload a video'); return; }
    setBusy(true); setProgress(0); setResult(null);
    try {
      const fd = new FormData();
      fd.append('video', file);
      if (studentId) fd.append('student_id', studentId);
      fd.append('teacher_notes', notes);
      fd.append('language', lang);
      const r = await api.post(`/exercises/${exoId}/analyze-video`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => { if (e.total) setProgress(Math.round((e.loaded / e.total) * 100)); },
      });
      setResult(r.data);
      toast.success(lang === 'ar' ? 'اكتمل التحليل' : 'Analysis complete');
    } catch (e) { toast.error(e.response?.data?.detail || 'Analysis failed'); }
    setBusy(false);
  };

  const downloadReport = () => {
    if (!result) return;
    const token = localStorage.getItem('token');
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/exercises/attempts/${result.id}/report-html`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(r => r.text()).then(html => { const w = window.open('', '_blank'); if (w) { w.document.write(html); w.document.close(); } });
  };

  return (
    <main id="main" className="max-w-7xl mx-auto px-4 py-8" data-testid="video-analyze-page">
      <Button asChild variant="ghost" size="sm" className="mb-4">
        <Link to="/exercises"><ArrowLeft className={`w-4 h-4 me-1 ${lang === 'ar' ? 'rotate-180' : ''}`} />{lang === 'ar' ? 'المكتبة' : 'Library'}</Link>
      </Button>

      <div className="mb-6 flex items-center gap-3 flex-wrap">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-600 to-rose-500 grid place-items-center text-white"><Video className="w-6 h-6" /></div>
        <div>
          <h1 className="text-2xl sm:text-3xl font-black">{lang === 'ar' ? 'تحليل أداء الطالب بالذكاء الاصطناعي' : 'AI Performance Review'}</h1>
          <p className="text-xs text-muted-foreground">{lang === 'ar' ? 'ارفع فيديو الأداء + اكتب ملاحظات سريعة → يقارن النظام بالتمرين المرجعي ويعطي تقريراً وتقييماً.' : 'Upload student video + brief notes → AI compares to reference and gives a report + score.'}</p>
        </div>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-6 text-xs text-amber-900 flex items-start gap-2" data-testid="ai-disclaimer">
        <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
        <div>{lang === 'ar' ? 'ملاحظة: هذا "تحليل بمساعدة AI". لا يقوم النظام بتحليل البصري لإطارات الفيديو، بل يولّد ملاحظات تصحيحية بناءً على التمرين المرجعي + ملاحظاتك. الفيديو يُحفظ كمرجع.' : 'Note: AI-Assisted review. The model does not analyze video frames; it compares the reference exercise to your written observations and produces corrective feedback. Video is saved as evidence.'}</div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-1">
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><Upload className="w-5 h-5 text-violet-600" />{lang === 'ar' ? 'الإدخال' : 'Inputs'}</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div>
              <Label className="text-xs">{lang === 'ar' ? 'التمرين المرجعي *' : 'Reference exercise *'}</Label>
              <Select value={exoId} onValueChange={setExoId}>
                <SelectTrigger data-testid="va-exercise"><SelectValue placeholder={lang === 'ar' ? 'اختر التمرين' : 'Pick'} /></SelectTrigger>
                <SelectContent>
                  {exercises.map(e => <SelectItem key={e.id} value={e.id}>{e.name_ar}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">{lang === 'ar' ? 'الطالب (اختياري)' : 'Student (optional)'}</Label>
              <Select value={studentId} onValueChange={setStudentId}>
                <SelectTrigger data-testid="va-student"><SelectValue placeholder={lang === 'ar' ? '— لا أحد —' : '— none —'} /></SelectTrigger>
                <SelectContent>
                  {students.map(s => <SelectItem key={s.id} value={s.id}>{s.full_name_ar}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">{lang === 'ar' ? 'فيديو الأداء *' : 'Performance video *'}</Label>
              <label className="block border-2 border-dashed border-violet-300 rounded-lg p-4 text-center cursor-pointer hover:bg-violet-50/40 transition" data-testid="va-file-label">
                <input type="file" accept="video/*" onChange={onFileChange} className="hidden" data-testid="va-file" />
                {file ? (
                  <div className="text-xs">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600 mx-auto mb-1" />
                    <div className="font-bold">{file.name}</div>
                    <div className="text-muted-foreground">{(file.size / 1024 / 1024).toFixed(1)} MB</div>
                  </div>
                ) : (
                  <div className="text-xs text-muted-foreground">
                    <Upload className="w-6 h-6 mx-auto mb-1 text-violet-500" />
                    {lang === 'ar' ? 'اضغط لاختيار فيديو (حد أقصى 100MB)' : 'Click to choose (max 100MB)'}
                  </div>
                )}
              </label>
            </div>
            <div>
              <Label className="text-xs">{lang === 'ar' ? 'ملاحظات المعلم على الأداء' : 'Teacher observation notes'}</Label>
              <Textarea rows={4} value={notes} onChange={e => setNotes(e.target.value)} placeholder={lang === 'ar' ? 'مثال: الطالب يميل للأمام، الذراعان غير ممدودتين، يفقد التوازن بعد 4 ثوانٍ...' : 'e.g. student leans forward, arms not extended, loses balance at 4s...'} data-testid="va-notes" />
            </div>
            {busy && progress < 100 && (
              <div className="space-y-1" data-testid="va-upload-progress">
                <div className="text-[10px] text-muted-foreground">{lang === 'ar' ? 'جاري الرفع' : 'Uploading'} · {progress}%</div>
                <Progress value={progress} className="h-1.5" />
              </div>
            )}
            <Button onClick={submit} disabled={busy || !file || !exoId} className="w-full bg-gradient-to-r from-violet-600 to-rose-500 hover:opacity-90" data-testid="va-submit">
              {busy ? <><Loader2 className="w-4 h-4 me-2 animate-spin" />{progress === 100 ? (lang === 'ar' ? 'يحلل...' : 'Analyzing...') : (lang === 'ar' ? 'يرفع...' : 'Uploading...')}</> : <><Sparkles className="w-4 h-4 me-2" />{lang === 'ar' ? 'حلّل الأداء' : 'Analyze'}</>}
            </Button>
          </CardContent>
        </Card>

        {/* Result panel */}
        <div className="lg:col-span-2 space-y-4">
          {filePreview && !result && (
            <Card><CardContent className="p-3">
              <video src={filePreview} controls className="w-full rounded-lg max-h-[300px]" data-testid="va-preview" />
            </CardContent></Card>
          )}
          {!result && !busy && (
            <Card className="border-dashed border-2"><CardContent className="p-12 text-center">
              <Video className="w-12 h-12 text-violet-400/40 mx-auto mb-3" />
              <p className="text-muted-foreground text-sm">{lang === 'ar' ? 'سيظهر التقرير هنا بعد التحليل.' : 'The AI report will appear here.'}</p>
            </CardContent></Card>
          )}
          {busy && !result && progress >= 100 && (
            <Card><CardContent className="p-12 text-center" data-testid="va-busy">
              <Loader2 className="w-12 h-12 animate-spin text-violet-600 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">{lang === 'ar' ? 'يحلّل المدرّب الذكي الأداء بناءً على التمرين المرجعي وملاحظاتك...' : 'AI coach is analyzing performance against the reference + your notes...'}</p>
            </CardContent></Card>
          )}
          {result && (
            <Card data-testid="va-result">
              <CardHeader className="border-b border-border">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Trophy className="w-5 h-5 text-amber-600" />
                    {lang === 'ar' ? 'تقرير الأداء' : 'Performance Report'}
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <Badge className={`text-base px-3 py-1 ${result.score >= 80 ? 'bg-emerald-600' : result.score >= 60 ? 'bg-amber-500' : 'bg-rose-500'}`} data-testid="va-score">
                      {result.score}/100
                    </Badge>
                    <Button size="sm" variant="outline" onClick={downloadReport} data-testid="va-download">
                      <FileText className="w-3.5 h-3.5 me-1" />{lang === 'ar' ? 'تقرير PDF' : 'PDF report'}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4 pt-4">
                {result.encouragement && (
                  <div className="bg-amber-50 border-2 border-amber-200 rounded-lg p-3 italic text-sm text-amber-900" data-testid="va-encouragement">
                    "{result.encouragement}"
                  </div>
                )}

                {(result.strengths || []).length > 0 && (
                  <div>
                    <h3 className="font-bold text-sm text-emerald-700 mb-1.5 flex items-center gap-1"><Award className="w-4 h-4" />{lang === 'ar' ? 'نقاط القوة' : 'Strengths'}</h3>
                    <ul className="space-y-1 text-sm">
                      {result.strengths.map((s, i) => <li key={i} className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />{s}</li>)}
                    </ul>
                  </div>
                )}

                {(result.errors || []).length > 0 && (
                  <div>
                    <h3 className="font-bold text-sm text-rose-700 mb-1.5 flex items-center gap-1"><AlertTriangle className="w-4 h-4" />{lang === 'ar' ? 'أخطاء وتصحيحات' : 'Errors & Fixes'}</h3>
                    <ul className="space-y-2" data-testid="va-errors">
                      {result.errors.map((e, i) => (
                        <li key={i} className="border-l-4 border-rose-500 bg-rose-50 rounded p-2.5 text-sm">
                          <div className="font-bold text-rose-700">{e.error}</div>
                          <div className="text-stone-700 mt-0.5">→ {e.fix}</div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {(result.next_drills || []).length > 0 && (
                  <div>
                    <h3 className="font-bold text-sm text-violet-700 mb-1.5 flex items-center gap-1"><ArrowRight className="w-4 h-4" />{lang === 'ar' ? 'تدريبات للمرحلة التالية' : 'Next drills'}</h3>
                    <ul className="space-y-1 text-sm">
                      {result.next_drills.map((s, i) => <li key={i} className="flex items-start gap-2"><span className="text-violet-600 font-black mt-0.5">→</span>{s}</li>)}
                    </ul>
                  </div>
                )}

                <div className="border-t border-border pt-3 text-xs text-muted-foreground flex items-center justify-between">
                  <span>{lang === 'ar' ? 'الفيديو محفوظ في النظام كدليل.' : 'Video saved as evidence.'}</span>
                  <Button asChild size="sm" variant="ghost"><Link to={`/exercises/${exoId}`}>{lang === 'ar' ? 'فتح التمرين المرجعي' : 'Open reference exercise'}<ArrowRight className="w-3.5 h-3.5 ms-1 rtl:rotate-180" /></Link></Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </main>
  );
}
